import { useEffect, useMemo, useState } from "react";
import type { BasicRow, RowMode } from "../types/basicQuote.types";
import { addBasicQuote, removeBasicQuote, selectBasicQuoteList, updateBasicQuote } from "../../../../../redux/features/quotes/quoteListSlice";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks/redux";

const createEmptyRow = (): BasicRow => ({
  id: crypto.randomUUID(),
  service: "",
  serviceUid: "",
  price: "",
  units: 1,
  measure: "",
  notes: "",
  alias: "",
  total: 0,
  frequencyType: "weekly",

  useServiceNameOverride: false,
  serviceNameOverride: "",

  mode: "editing",
});

export const useQuoteBasicList = ({ onPayloadChange, onlyOneService, initialRows }: any) => {
  const [rows, setRows] = useState<BasicRow[]>(() => (initialRows?.length ? initialRows : [createEmptyRow()]));

  const dispatch = useAppDispatch();
  const basicList = useAppSelector(selectBasicQuoteList);

  useEffect(() => {
    if (initialRows?.length) return;

    // Solo reseteamos el estado local si Redux se vació por completo
    // Y si actualmente tenemos filas que no coinciden con ese estado vacío
    if (basicList.length === 0 && rows.length > 0 && rows[0].service !== "") {
      setRows([createEmptyRow()]);
    }
    // Añadimos basicList.length y rows para que el efecto sea preciso
  }, [basicList.length, initialRows?.length]);

  useEffect(() => {
    if (typeof onPayloadChange === "function") {
      onPayloadChange(rows);
    }
  }, [rows]);

  const basicMapById = useMemo(() => {
    const map: Record<string, any> = {};
    basicList.forEach((q: any) => {
      if (q.id) map[q.id] = q;
    });
    return map;
  }, [basicList]);

  const addRow = () => {
    setRows((prev) => [
      createEmptyRow(),
      ...prev, // <--- Los anteriores van después del nuevo
    ]);
  };

  const setRowMode = (index: number, mode: RowMode) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, mode } : row)));
  };

  const updateRowField = (index: number, field: keyof BasicRow, value: any) => {
    setRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index], [field]: value };

      // Recalcular total
      if (field === "price" || field === "units") {
        row.total = Number(row.price || 0) * Number(row.units || 0);
      }

      updated[index] = row;
      return updated;
    });
  };

  const deleteRowLocalAndRedux = (index: number) => {
    const row = rows[index];

    const reduxIndex = basicList.findIndex((q: any) => q.id === row.id);
    if (reduxIndex !== -1) {
      dispatch(removeBasicQuote(reduxIndex));
    }

    setRows((prev) => prev?.filter((_, i) => i !== index));
  };

  const handleSaveAndLockRow = (index: number, isCompact: boolean = false) => {
    const row = rows[index];

    if (!row.service) {
      console.warn("Service requerido");
      return;
    }
    if (!isCompact && !row.price) {
      console.warn("Price requerido");
      return;
    }

    const payload = {
      id: row.id,
      serviceUid: row.serviceUid,
      service: row.service,
      price: row.price || 0,
      units: row.units || 1,
      measure: row.measure || "unit",
      notes: row.notes,
      alias: row.alias,
      total: row.total || 0,
      frequencyType: row.frequencyType || "weekly",

      useServiceNameOverride: row.useServiceNameOverride,
      serviceNameOverride: row.useServiceNameOverride ? row.serviceNameOverride?.trim() || "" : "",
    };

    const reduxIndex = basicList.findIndex((q: any) => q.id === row.id);
    if (reduxIndex === -1) {
      dispatch(addBasicQuote(payload));
    } else {
      dispatch(updateBasicQuote({ index: reduxIndex, updates: payload }));
    }

    setRowMode(index, "locked");
  };

  const handleEnterEditMode = (index: number) => {
    setRowMode(index, "editing");
  };

  const handleRemoveRowFromRedux = (rowId: string) => {
    const reduxIndex = basicList.findIndex((q: any) => q.id === rowId);
    if (reduxIndex !== -1) {
      dispatch(removeBasicQuote(reduxIndex));
    }

    setRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, mode: "editing" } : row)));
  };

  return {
    rows,
    addRow,
    deleteRowLocalAndRedux,
    updateRowField,
    setRowMode,
    handleSaveAndLockRow,
    handleEnterEditMode,
    handleRemoveRowFromRedux,
    basicMapById,
  };
};
